import React, { useCallback, useContext } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { User } from 'models/User';
import ContactUserInfo from '../../ContactUserInfo';
import GroupUsersIcon from 'icons/GroupUsers.icon';
import { getArrayLength, getHasElementsInArray } from 'utils';
import Counter from 'components/Counter';
import { COUNTER_VARIANTS } from 'components/Counter/Counter.component';
import CurrentUserContext from 'providers/CurrentUser/CurrentUser.context';

import './GroupMembers.styles.scss';

type GroupMembersProps = {
  className?: string;
  sellRepresentatives: User[];
  buyRepresentatives: User[];
};

const GroupMembers: React.FC<GroupMembersProps> = (props) => {
  const {
    className,
    sellRepresentatives = [],
    buyRepresentatives = [],
  } = props;

  const count = getArrayLength([...sellRepresentatives, ...buyRepresentatives]);

  const classes = classNames('group-members', className);

  const { t } = useTranslation();

  const { currentUser } = useContext(CurrentUserContext);

  const currentUserId = currentUser?._id;

  const renderSection = useCallback(
    (title: string, members: User[]) => {
      if (!getHasElementsInArray(members)) return null;

      const sortedMembers = [...members].sort((first, second) => {
        if (first._id === currentUserId) return -1;
        if (second._id === currentUserId) return 1;
        return 0;
      });

      return (
        <section>
          <p>
            {title}
            <Counter
              count={getArrayLength(members)}
              size="small"
              variant={COUNTER_VARIANTS.GREY}
            />
          </p>
          <ul className="group-members__list">
            {sortedMembers.map((member) => (
              <ContactUserInfo
                key={member._id}
                user={member}
                isCurrentUser={member._id === currentUserId}
              />
            ))}
          </ul>
        </section>
      );
    },
    [currentUserId],
  );

  return (
    <div className={classes}>
      <div className="group-members__title">
        <span>{t('groupChatWith')}</span>
        <p>
          <GroupUsersIcon />
          <span>
            {t('numberOfPeople', {
              count,
            })}
          </span>
        </p>
      </div>

      {renderSection(t('sellSide'), sellRepresentatives)}
      {renderSection(t('buySide'), buyRepresentatives)}
    </div>
  );
};

export default GroupMembers;
